// client/src/components/templates/UseTemplateModal.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, ShoppingCart, AlertCircle, CheckCircle, TrendingUp, TrendingDown, Package, Sparkles, ArrowRight, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { orderTemplatesAPI } from '../../services/orderTemplatesAPI';
import { productDiscountsAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
// ✅ FIX: Import helper
import { getImageUrl } from '../../utils/imageHelper';

const UseTemplateModal = ({ template, onClose, onSuccess }) => {
  const { t } = useTranslation(['business', 'common']);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [loading, setLoading] = useState(true);
  const [templateData, setTemplateData] = useState(null);
  const [error, setError] = useState(null);
  const [productDiscountsByWholesaler, setProductDiscountsByWholesaler] = useState({}); // { wholesalerId: { productId: { percentage, reason } } }

  useEffect(() => {
    loadTemplateData();
  }, [template._id]);

  const loadTemplateData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderTemplatesAPI.use(template._id);
      setTemplateData(response);

      // Fetch product discounts for each wholesaler in this template
      const ordersByW = response?.ordersByWholesaler || {};
      const pdMap = {};
      await Promise.all(Object.keys(ordersByW).map(async (wid) => {
        try {
          const res = await productDiscountsAPI.getMyDiscounts(String(wid));
          if (res.data.success && res.data.discounts && typeof res.data.discounts === 'object') {
            pdMap[String(wid)] = res.data.discounts;
          }
        } catch (e) { /* silent */ }
      }));
      setProductDiscountsByWholesaler(pdMap);
    } catch (error) {
      console.error('Error loading template:', error);
      setError(error.response?.data?.error || t('business:templates.modal.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!templateData?.orderItems || templateData.orderItems.length === 0) {
      toast.error(t('templates.modal.noProductsAvailable'));
      return;
    }

    let addedCount = 0;
    const ordersByWholesaler = templateData.ordersByWholesaler || {};
    
    templateData.orderItems.forEach(item => {
      try {
        const wholesalerId = Object.keys(ordersByWholesaler).find(wId => 
          ordersByWholesaler[wId].items.some(i => i.productId === item.productId)
        );
        const wholesalerName = ordersByWholesaler[wholesalerId]?.wholesalerName || 'Unknown';
        
        addToCart({
          _id: item.productId,
          name: item.name,
          sku: item.sku,
          pricing: {
            unitPrice: item.unitPrice,
            unit: item.unit
          },
          images: item.images,
          category: item.category,
          wholesalerId: wholesalerId || templateData.wholesalerId,
          wholesalerName: wholesalerName
        }, item.quantity);
        addedCount++;
      } catch (err) {
        console.error('Error adding item to cart:', err);
      }
    });

    if (addedCount > 0) {
      toast.success(t('templates.modal.successUse'));
      onSuccess();
    } else {
      toast.error(t('templates.modal.errorUse'));
    }
  };

  const handleGoToCart = () => {
    handleAddToCart();
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="relative mb-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
            <Sparkles className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-600 font-medium">{t('business:templates.modal.loading')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('business:templates.modal.loadingProducts')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('business:templates.modal.error')}</h3>
            <p className="text-gray-600">{error}</p>
          </div>
          <button onClick={onClose} className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold">{t('common:templateModal.close')}</button>
        </div>
      </div>
    );
  }

  const stats = templateData?.statistics || {};
  const availableItems = templateData?.orderItems || [];
  const unavailableItems = templateData?.unavailableItems || [];
  const priceChanges = templateData?.priceChanges || [];
  const ordersByWholesaler = templateData?.ordersByWholesaler || {};

  const availableByWholesaler = availableItems.reduce((acc, item) => {
    const wholesalerId = Object.keys(ordersByWholesaler).find(wId => 
      ordersByWholesaler[wId].items.some(i => i.productId === item.productId)
    );
    const wholesalerName = ordersByWholesaler[wholesalerId]?.wholesalerName || 'Unknown';
    
    if (!acc[wholesalerId]) {
      acc[wholesalerId] = {
        name: wholesalerName,
        hidePricing: ordersByWholesaler[wholesalerId]?.hidePricing || false,
        items: []
      };
    }
    acc[wholesalerId].items.push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="relative bg-gradient-to-r from-green-600 via-emerald-500 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <ShoppingCart className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{templateData.templateName}</h2>
                <p className="text-green-100 text-sm mt-1">{t('business:templates.modal.subtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-6 h-6" /></button>
          </div>
        </div>

        <div className="w-full sm:flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
              <p className="text-sm text-blue-700 font-semibold mb-1">{t('business:templates.modal.totalProducts')}</p>
              <p className="text-3xl font-bold text-blue-900">{stats.totalItems || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
              <p className="text-sm text-green-700 font-semibold mb-1">{t('business:templates.modal.products.available')}</p>
              <p className="text-3xl font-bold text-green-900">{stats.availableItems || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200">
              <p className="text-sm text-purple-700 font-semibold mb-1">{t('business:templates.wholesalersCount')}</p>
              <p className="text-3xl font-bold text-purple-900">{stats.wholesalerCount || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
              <p className="text-sm text-orange-700 font-semibold mb-1">{t('business:templates.modal.totalValue')}</p>
              <p className="text-3xl font-bold text-orange-900">
                {Object.values(ordersByWholesaler).some(g => g.hidePricing) ? '—' : `€${(stats.totalValue || 0).toFixed(2)}`}
              </p>
            </div>
          </div>

          {unavailableItems.length > 0 && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-4 flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center"><AlertCircle className="w-6 h-6 text-yellow-900" /></div>
              </div>
              <div>
                <p className="font-bold text-yellow-900 mb-1">{t('common:templateModal.itemsUnavailable', { count: unavailableItems.length })}</p>
                <p className="text-sm text-yellow-800">{t('common:templateModal.itemsUnavailableDesc')}</p>
              </div>
            </div>
          )}

          {priceChanges.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-400 rounded-lg flex items-center justify-center"><TrendingUp className="w-6 h-6 text-blue-900" /></div>
              </div>
              <div>
                <p className="font-bold text-blue-900 mb-1">{t('common:templateModal.priceChangesDetected')}</p>
                <p className="text-sm text-blue-800">{t('common:templateModal.priceChangesDesc')}</p>
              </div>
            </div>
          )}

          {Object.keys(availableByWholesaler).length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-lg"><CheckCircle className="w-5 h-5 text-green-600" /></div>
                {t('business:templates.modal.products.available')} ({availableItems.length})
              </h3>
              
              {Object.entries(availableByWholesaler).map(([wholesalerId, group]) => (
                <div key={wholesalerId} className="mb-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg border border-indigo-200 mb-2">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-white" /></div>
                    <span className="font-bold text-indigo-900">{group.name}</span>
                    <span className="ml-auto text-sm text-indigo-700">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
                  </div>

                  <div className="space-y-3">
                    {group.items.map((item, index) => (
                      <div key={index} className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 hover:border-green-300 transition-all">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <img
                              src={getImageUrl(item.images?.[0])}
                              alt={item.name}
                              className="w-12 h-12 sm:w-16 sm:h-16 sm:w-20 sm:h-20 object-cover rounded-lg border-2 border-white shadow-md"
                              onError={(e) => {
                                if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled = 'true'; e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3Cpath fill=%22%239ca3af%22 d=%22M30 35h40v5H30zm0 10h40v5H30zm0 10h25v5H30z%22/%3E%3Ccircle fill=%22%23d1d5db%22 cx=%2250%22 cy=%2265%22 r=%228%22/%3E%3C/svg%3E'; }
                              }}
                            />
                          </div>

                          <div className="w-full sm:flex-1 min-w-0">
                            <p className="font-bold text-gray-900 mb-1">{item.name}</p>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-white rounded-md text-sm font-semibold text-gray-700">{t('common:templateModal.sku')}: {item.sku || t('common:templateModal.na')}</span>
                              {item.category && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-semibold">{item.category}</span>}
                            </div>
                            {item.notes && <p className="text-sm text-gray-600 italic bg-white px-3 py-1 rounded-md inline-block">💭 {item.notes}</p>}
                          </div>

                          <div className="text-right sm:ml-4 w-full sm:w-auto sm:flex-shrink-0 mt-3 sm:mt-0">
                            {!group.hidePricing && (
                            <div className="bg-white rounded-lg px-3 py-2 mb-2 border-2 border-gray-200">
                              <p className="text-xs text-gray-500">{t('business:templates.modal.price')}</p>
                              <p className="font-bold text-gray-900">€{item.unitPrice.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">{t('common:templateModal.per')} {item.unit}</p>
                              {productDiscountsByWholesaler[wholesalerId]?.[item.productId] && (
                                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
                                  -{productDiscountsByWholesaler[wholesalerId][item.productId].percentage}%
                                </span>
                              )}
                            </div>
                            )}
                            <div className="bg-green-100 rounded-lg px-3 py-2 border-2 border-green-300">
                              <p className="text-xs text-green-700 font-semibold">{t('business:templates.modal.quantity')}: {item.quantity}</p>
                              {!group.hidePricing && <p className="text-lg font-bold text-green-900">€{item.totalPrice.toFixed(2)}</p>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {unavailableItems.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="p-1.5 bg-red-100 rounded-lg"><AlertCircle className="w-5 h-5 text-red-600" /></div>
                {t('common:templateModal.unavailableItems')} ({unavailableItems.length})
              </h3>
              <div className="space-y-3">
                {unavailableItems.map((item, index) => (
                  <div key={index} className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{item.name}</p>
                        <p className="text-sm text-red-600 mt-1 flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"></span>{item.reason}</p>
                      </div>
                      <p className="text-sm text-gray-500 ml-4 bg-white px-3 py-1 rounded-lg">{t('common:templateModal.sku')}: {item.sku}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {availableItems.length === 0 && unavailableItems.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 sm:w-16 sm:h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">{t('business:templates.noProducts')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('business:templates.noProductsAdded')}</p>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t-2 border-gray-100 bg-gradient-to-r from-gray-50 to-green-50">
          {availableItems.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">{t('common:adding')} {t('business:templates.items', { count: availableItems.length })} {t('common:toCart')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{unavailableItems.length > 0 && `${unavailableItems.length} ${t('business:templates.modal.products.unavailable')} ${t('common:willBeSkipped')}`}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{t('business:templates.modal.total')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {Object.values(ordersByWholesaler).some(g => g.hidePricing) ? '—' : `€${(stats.totalValue || 0).toFixed(2)}`}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={handleAddToCart} className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"><ShoppingCart className="w-5 h-5" />{t('business:templates.modal.addToCart')}</button>
                <button onClick={handleGoToCart} className="px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2">{t('common:buttons.goToCart')}<ArrowRight className="w-5 h-5" /></button>
              </div>
            </div>
          ) : (
            <button onClick={onClose} className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all font-semibold">{t('common:templateModal.close')}</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UseTemplateModal;